import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import Divider from "@mui/material/Divider"
import InputAdornment from "@mui/material/InputAdornment"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDebounceValue } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import SearchSuggestionsDropdown from "./SearchSuggestionsDropdown"
import {
  SEARCH_BY_OPTIONS,
  SEARCH_BY_PLACEHOLDERS,
  SUGGEST_MIN_CHARS,
} from "./data"
import {
  getSearchByOption,
  getSeeAllPath,
  getSuggestionPath,
} from "./highlight"

const suggestCache = new Map()

export default function GlobalSearchField({
  width,
  maxWidth,
  onSearchResultSelected = () => {},
}) {
  const [searchValue, setSearchValue] = useState("")
  const [searchBy, setSearchBy] = useState("all")
  const debouncedSearchValue = useDebounceValue(searchValue, 300)[0]
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [searchByAnchor, setSearchByAnchor] = useState(null)
  const [inputFocused, setInputFocused] = useState(false)
  const requestIdRef = useRef(0)
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()
  const selectedSearchBy = getSearchByOption(SEARCH_BY_OPTIONS, searchBy)

  function resetSearch() {
    setDropdownOpen(false)
    setSuggestions([])
    setSearchValue("")
    setSelectedIndex(0)
    setIsLoading(false)
  }

  function handleSuggestionClicked(item) {
    const path = getSuggestionPath(item)
    resetSearch()
    onSearchResultSelected(item)
    if (path) {
      navigate(path)
    }
  }

  function handleSeeAllClicked() {
    const query = searchValue.trim()
    resetSearch()
    onSearchResultSelected()
    navigate(getSeeAllPath(searchBy, query))
  }

  function handleSearchBySelected(value) {
    setSearchBy(value)
    setSearchByAnchor(null)
    setSelectedIndex(0)
  }

  function handleKeyDown(event) {
    if (!dropdownOpen) {
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setSelectedIndex((index) =>
        suggestions.length ? (index + 1) % suggestions.length : 0
      )
      return
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      setSelectedIndex((index) =>
        suggestions.length
          ? (index - 1 + suggestions.length) % suggestions.length
          : 0
      )
      return
    }
    if (event.key === "Enter" && suggestions[selectedIndex]) {
      event.preventDefault()
      handleSuggestionClicked(suggestions[selectedIndex])
      return
    }
    if (event.key === "Escape") {
      setDropdownOpen(false)
    }
  }

  useEffect(
    () => {
      const query = (debouncedSearchValue || "").trim()
      if (query.length < SUGGEST_MIN_CHARS) {
        setSuggestions([])
        setDropdownOpen(false)
        setIsLoading(false)
        return
      }

      const cacheKey = `${searchBy}:${query.toLowerCase()}`
      if (suggestCache.has(cacheKey)) {
        setSuggestions(suggestCache.get(cacheKey))
        setSelectedIndex(0)
        setDropdownOpen(true)
        setIsLoading(false)
        return
      }

      const controller = new AbortController()
      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      setIsLoading(true)
      axios
        .get("/crm/api/global-search/suggest/", {
          params: { q: query, type: searchBy },
          signal: controller.signal,
        })
        .then(({ data }) => {
          if (requestId !== requestIdRef.current) {
            return
          }
          const results = Array.isArray(data) ? data : []
          suggestCache.set(cacheKey, results)
          setSuggestions(results)
          setSelectedIndex(0)
          setDropdownOpen(true)
        })
        .catch((error) => {
          if (error.code === "ERR_CANCELED") {
            return
          }
          if (requestId !== requestIdRef.current) {
            return
          }
          setSuggestions([])
          setDropdownOpen(true)
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setIsLoading(false)
          }
        })

      return () => controller.abort()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchValue, searchBy]
  )

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (searchByAnchor) {
          return
        }
        setDropdownOpen(false)
      }}
    >
      <SearchFieldRoot
        $elevated={inputFocused || dropdownOpen || Boolean(searchByAnchor)}
        sx={{ width, maxWidth }}
      >
        <SearchTextField
          placeholder={SEARCH_BY_PLACEHOLDERS[searchBy]}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => {
            setInputFocused(true)
            if ((searchValue || "").trim().length >= SUGGEST_MIN_CHARS) {
              setDropdownOpen(true)
            }
          }}
          onBlur={() => setInputFocused(false)}
          onKeyDown={handleKeyDown}
          type="search"
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: "2px" }}>
                <SearchIconBadge>
                  <SearchOutlinedIcon sx={{ fontSize: 18 }} />
                </SearchIconBadge>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end" sx={{ mr: "-2px" }}>
                {isLoading ? (
                  <CircularProgress
                    variant="indeterminate"
                    color="inherit"
                    size={16}
                    thickness={5}
                    sx={{ mr: 1 }}
                  />
                ) : null}
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    height: 22,
                    alignSelf: "center",
                    mr: 0.75,
                    borderColor: "colors.brand200",
                  }}
                />
                <SearchByButton
                  size="small"
                  color="inherit"
                  $active={searchBy !== "all"}
                  endIcon={<KeyboardArrowDown />}
                  onClick={(event) => setSearchByAnchor(event.currentTarget)}
                  aria-haspopup="listbox"
                  aria-expanded={Boolean(searchByAnchor)}
                  aria-label={`Search by ${selectedSearchBy.label}`}
                >
                  {selectedSearchBy.buttonLabel}
                </SearchByButton>
              </InputAdornment>
            ),
          }}
          size="small"
          fullWidth
        />
        <Menu
          anchorEl={searchByAnchor}
          open={Boolean(searchByAnchor)}
          onClose={() => setSearchByAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ sx: { py: 0.5 } }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 188,
              padding: "6px !important",
              borderRadius: "14px",
              border: "1px solid",
              borderColor: "colors.brand200",
              boxShadow:
                "0 16px 32px -12px rgba(147, 32, 213, 0.2), 0 6px 12px -6px rgba(16, 24, 40, 0.12)",
              "&:before": { display: "none" },
            },
          }}
        >
          {SEARCH_BY_OPTIONS.map((option) => (
            <SearchByMenuItem
              key={option.value}
              selected={option.value === searchBy}
              onClick={() => handleSearchBySelected(option.value)}
            >
              {option.label}
            </SearchByMenuItem>
          ))}
        </Menu>
        {dropdownOpen ? (
          <SearchSuggestionsDropdown
            suggestions={suggestions}
            searchValue={searchValue}
            searchBy={searchBy}
            selectedIndex={selectedIndex}
            onSuggestionClicked={handleSuggestionClicked}
            onSeeAllClicked={handleSeeAllClicked}
          />
        ) : null}
      </SearchFieldRoot>
    </ClickAwayListener>
  )
}

const SearchFieldRoot = styled("div")(({ $elevated }) => ({
  position: "relative",
  transition: "transform 160ms ease",
  transform: $elevated ? "translateY(-1px)" : "none",
}))

const SearchTextField = styled(TextField)(({ theme }) => ({
  paddingBlock: 2,
  "& .MuiInputBase-root": {
    minHeight: 46,
    borderRadius: 28,
    paddingRight: "8px !important",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 28,
    border: `1px solid ${theme.palette.colors.brand200}`,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(251,243,255,0.88) 100%)",
    boxShadow:
      "0 10px 24px -10px rgba(147, 32, 213, 0.28), 0 2px 6px rgba(16, 24, 40, 0.06)",
  },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `1.5px solid ${theme.palette.colors.brand500}`,
    boxShadow:
      "0 0 0 4px rgba(147, 32, 213, 0.12), 0 14px 28px -10px rgba(147, 32, 213, 0.32)",
  },
  "& .MuiInputBase-input": {
    fontWeight: 500,
    "&::placeholder": {
      color: theme.palette.colors.gray500,
      opacity: 1,
    },
  },
}))

const SearchIconBadge = styled("div")(({ theme }) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #9320D5 0%, #FF00D3 100%)",
  color: "#fff",
  boxShadow: "0 4px 10px rgba(147, 32, 213, 0.35)",
}))

const SearchByButton = styled(Button)(({ theme, $active }) => ({
  minWidth: 0,
  padding: theme.spacing(0.4, 1),
  textTransform: "none",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.01em",
  color: $active ? theme.palette.colors.brand700 : theme.palette.colors.gray700,
  backgroundColor: $active
    ? theme.palette.colors.brand50
    : theme.palette.colors.gray50,
  borderRadius: 999,
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: $active
      ? theme.palette.colors.brand100
      : theme.palette.colors.gray100,
  },
  "& .MuiButton-endIcon": {
    marginLeft: 2,
  },
}))

const SearchByMenuItem = styled(MenuItem)(({ theme }) => ({
  borderRadius: 10,
  margin: theme.spacing(0.15, 0.4),
  fontSize: 13,
  fontWeight: 600,
  "&.Mui-selected": {
    backgroundColor: theme.palette.colors.brand50,
    color: theme.palette.colors.brand700,
  },
  "&.Mui-selected:hover": {
    backgroundColor: theme.palette.colors.brand100,
  },
}))
